/**
 * The Turnstile challenge, shown once before an anonymous visitor's first
 * message.
 *
 * WHY A VISITOR SEES THIS AT ALL
 * Chat needs no account by design, which means the only thing identifying an
 * anonymous caller is a session id their own browser generated — and a script
 * can generate a new one per request, which is exactly how the per-visitor rate
 * limit came to bound honest people and nobody else. This is the cheapest check
 * that costs a person nothing and a script a great deal, without a signup.
 *
 * It appears ONCE per thread (twelve hours), never for a signed-in visitor, and
 * never on a deployment with no Turnstile configured — `conciergeAgent.thread`
 * decides all three and hands down a single boolean.
 *
 * The script is loaded lazily, on first render of this component, so a visitor
 * who never opens the concierge never pays for it. `challenges.cloudflare.com`
 * is already allowed by the production CSP in `amplify.yml` (Clerk uses
 * Turnstile too) and was added to the dev CSP in `vite.config.ts` alongside it.
 */

import { useEffect, useRef, useState } from 'react';
import { warn } from '@/lib/logger';

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-script';

interface TurnstileApi {
  render: (
    el: HTMLElement,
    options: {
      sitekey: string;
      callback: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
      theme?: 'auto' | 'light' | 'dark';
      size?: 'normal' | 'flexible' | 'compact';
    },
  ) => string;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/** Load the script once per page, sharing the promise across callers. */
let scriptPromise: Promise<void> | null = null;

function loadTurnstile(): Promise<void> {
  if (scriptPromise) {
    return scriptPromise;
  }
  scriptPromise = new Promise<void>((resolve, reject) => {
    if (window.turnstile) {
      resolve();
      return;
    }
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('turnstile failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('turnstile failed to load'));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

interface HumanCheckProps {
  /** Called with the token once the visitor passes. */
  onVerified: (token: string) => void;
}

export function HumanCheck({ onVerified }: HumanCheckProps): JSX.Element | null {
  const holderRef = useRef<HTMLDivElement | null>(null);
  const renderedRef = useRef(false);
  const [failed, setFailed] = useState(false);

  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

  useEffect(() => {
    if (!siteKey || renderedRef.current) {
      return;
    }
    let cancelled = false;

    void loadTurnstile()
      .then(() => {
        if (cancelled || !holderRef.current || !window.turnstile || renderedRef.current) {
          return;
        }
        renderedRef.current = true;
        window.turnstile.render(holderRef.current, {
          sitekey: siteKey,
          theme: 'dark',
          size: 'flexible',
          callback: (token: string) => onVerified(token),
          'error-callback': () => setFailed(true),
          // A pass is good for the thread, but Cloudflare's own token expires
          // sooner; let it re-run rather than leaving a dead widget on screen.
          'expired-callback': () => {
            renderedRef.current = false;
          },
        });
      })
      .catch((cause: unknown) => {
        warn('Turnstile did not load', {
          reason: cause instanceof Error ? cause.message : String(cause),
        });
        setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [onVerified, siteKey]);

  // No site key configured in this build. The backend independently routes
  // anonymous visitors to the scripted concierge in that state, so this is a
  // belt-and-braces return rather than the real gate.
  if (!siteKey) {
    return null;
  }

  return (
    // `relative` is load-bearing, not decoration: it lifts this block above the
    // absolutely-positioned `.iv-glass-refract-layer` sibling. Without it the
    // block shares a paint layer with that overlay, whose
    // `backdrop-filter: url(#iv-glass-displace)` then DISPLACES this text —
    // rendering it doubled and offset. Every other content block in the panel
    // carries `relative` for the same reason; this one was missed.
    <div className="relative px-3 pb-2">
      <p className="mb-1.5 text-[11px] text-muted-foreground">
        One quick check before we start — it keeps the concierge free to use.
      </p>
      <div ref={holderRef} />
      {failed && (
        <p className="mt-1 text-[11px] text-muted-foreground">
          That check could not load. The contact page reaches a person directly.
        </p>
      )}
    </div>
  );
}
